import { Check, TrendingUp, Flame, AlertCircle, Loader } from "lucide-react";
import { useApp } from '@/context/app-context';
import { Logo } from '@/components/logo';
import { calcE1RM, toDateStr } from '@/lib/helpers';

export function Stats() {
  const { state, insights, sheetsStatus } = useApp();

  // Sync badge logic
  const getSyncBadgeStyle = () => {
    if (!state.gapiToken) {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    if (sheetsStatus === 'syncing') {
      return { bgColor: "#78350F", textColor: "#FCD34D", icon: Loader, label: "Syncing..." };
    }
    if (sheetsStatus === 'error') {
      return { bgColor: "#7F1D1D", textColor: "#FCA5A5", icon: AlertCircle, label: "Sync Error" };
    }
    if (sheetsStatus === 'paused') {
      return { bgColor: "#3F3F46", textColor: "#A1A1AA", icon: null, label: "Offline" };
    }
    return { bgColor: "#1F4428", textColor: "#4ADE80", icon: Check, label: "Synced" };
  };

  const badgeStyle = getSyncBadgeStyle();
  const BadgeIcon = badgeStyle.icon;

  if (!insights) {
    return (
      <div className="p-5 pb-24" style={{ backgroundColor: '#18181B' }}>
        <div className="flex items-center justify-between mb-5" style={{ height: '32px' }}>
          <Logo />
          <div
            className="px-2.5 py-1 rounded-full flex items-center gap-1"
            style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor, fontSize: '11px', fontWeight: 600 }}
          >
            {BadgeIcon && <BadgeIcon size={10} />}
            <span>{badgeStyle.label}</span>
          </div>
        </div>
        <h2 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
          Stats
        </h2>
        <p style={{ color: '#71717A', fontSize: '14px' }}>No data yet. Start tracking workouts to see insights.</p>
      </div>
    );
  }

  // Extract frequency data from history
  const weeklyData = Array.from({ length: 12 }, (_, i) => {
    const weeks = 11 - i;
    const cutoff = new Date();
    const weekStart = new Date(cutoff);
    weekStart.setDate(cutoff.getDate() - weeks * 7);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    const startStr = toDateStr(weekStart);
    const endStr = toDateStr(weekEnd);
    const count = new Set(
      (state.history || [])
        .filter((h) => h.date >= startStr && h.date <= endStr)
        .map((h) => h.date),
    ).size;
    return count;
  });

  // Activity heatmap (8 weeks x 7 days)
  const heatmapData = Array.from({ length: 8 }, (_, weekIdx) => {
    const weeks = 7 - weekIdx;
    return Array.from({ length: 7 }, (_, dayIdx) => {
      const date = new Date();
      date.setDate(date.getDate() - (weeks * 7 - (6 - dayIdx)));
      const dateStr = toDateStr(date);
      return (state.history || []).some((h) => h.date === dateStr);
    });
  });

  // Count PRs this month
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthStartStr = toDateStr(monthStart);

  const prCount = new Set(
    (state.history || [])
      .filter((h) => h.date >= monthStartStr)
      .flatMap((h) =>
        (h.exercises || []).flatMap((ex) =>
          (ex.sets || []).map((s) => `${ex.name}-${calcE1RM(s.weight, s.reps)}`)
        )
      )
  ).size;

  // Calculate average frequency per week
  const totalWorkouts = new Set((state.history || []).map((h) => h.date)).size;
  const weeksSinceFirstWorkout = Math.max(1, Math.ceil((state.history?.[0]?.date ?
    (now.getTime() - new Date(state.history[0].date).getTime()) / (7 * 24 * 60 * 60 * 1000) : 1)));
  const avgFrequency = (totalWorkouts / weeksSinceFirstWorkout).toFixed(1);

  // Body weight data for composition chart
  const bodyCompositionData = (state.weightLog || []).slice(-10).map((entry) => ({
    date: entry.date,
    weight: entry.weight,
  }));

  // Calculate body weight trend for display
  const latestWeight = bodyCompositionData.length > 0
    ? bodyCompositionData[bodyCompositionData.length - 1].weight
    : state.bodyStats.weight || 0;

  // Group PRs by muscle category (using simple categorization)
  const exerciseToCategory: Record<string, string> = {
    'Leg Press': 'LEGS',
    'Leg Extension': 'LEGS',
    'Squats': 'LEGS',
    'Hack Squat': 'LEGS',
    'Leg Curl': 'LEGS',
    'Calf Raises': 'CALVES',
    'Bench Press': 'CHEST',
    'Incline Press': 'CHEST',
    'Chest Flyes': 'CHEST',
    'Dumbbell Press': 'CHEST',
    'Barbell Row': 'BACK',
    'Pull-Ups': 'BACK',
    'Lat Pulldown': 'BACK',
    'Deadlift': 'BACK',
    'Shoulder Press': 'SHOULDERS',
    'Lateral Raises': 'SHOULDERS',
  };

  // Extract PR data from progressive overload insights
  const prsByCategory: Record<string, Array<{ name: string; value: string; delta: string; isNew: boolean }>> = {};

  insights.overload.forEach((exercise) => {
    const category = exerciseToCategory[exercise.name] || 'OTHER';
    if (!prsByCategory[category]) {
      prsByCategory[category] = [];
    }
    const e1rmDisplay = `${Math.round(exercise.lastE1RM)}${state.unitPreference === 'kg' ? 'kg' : 'lbs'}`;
    const deltaDisplay = `${exercise.pctChange > 0 ? '+' : ''}${exercise.pctChange}%`;
    prsByCategory[category].push({
      name: exercise.name,
      value: e1rmDisplay,
      delta: deltaDisplay,
      isNew: exercise.sessions === 1,
    });
  });

  const personalRecords = Object.entries(prsByCategory)
    .map(([category, records]) => ({
      category,
      records: records.slice(0, 3), // Limit to 3 per category for display
    }))
    .slice(0, 4); // Limit to 4 categories

  // Intensity calculation based on RIR distribution
  const intensityValues: number[] = [];
  (state.history || []).forEach((h) => {
    (h.exercises || []).forEach((ex) => {
      (ex.sets || []).forEach((s) => {
        if (s.rir !== undefined) {
          intensityValues.push(s.rir);
        }
      });
    });
  });

  const avgIntensity = intensityValues.length > 0
    ? intensityValues.reduce((a, b) => a + b, 0) / intensityValues.length
    : 2;

  const intensityLabel = avgIntensity < 1.5 ? 'Hard' : avgIntensity < 2.5 ? 'Moderate' : 'Light';
  const intensityPercent = Math.min(100, Math.max(0, (3 - avgIntensity) / 3 * 100));

  return (
    <div className="p-5 pb-24" style={{ backgroundColor: '#18181B' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5" style={{ height: '32px' }}>
        <Logo />
        <div
          className="px-2.5 py-1 rounded-full flex items-center gap-1"
          style={{ backgroundColor: badgeStyle.bgColor, color: badgeStyle.textColor, fontSize: '11px', fontWeight: 600 }}
        >
          {BadgeIcon && <BadgeIcon size={10} />}
          <span>{badgeStyle.label}</span>
        </div>
      </div>

      {/* Title */}
      <h2 style={{ color: '#F0F0F0', fontSize: '18px', fontWeight: 600, marginBottom: '20px' }}>
        Stats
      </h2>

      {/* Summary Pills */}
      <div className="flex gap-3 mb-5 overflow-x-auto">
        <div
          className="px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap"
          style={{ backgroundColor: '#27272A' }}
        >
          <Flame size={14} style={{ color: '#FF6B35' }} />
          <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600 }}>
            {insights.streak.current} day streak
          </span>
        </div>
        <div
          className="px-4 py-2 rounded-full whitespace-nowrap"
          style={{ backgroundColor: '#1F4428', color: '#4ADE80', fontSize: '13px', fontWeight: 600 }}
        >
          {prCount} PRs this mo.
        </div>
        <div
          className="px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap"
          style={{ backgroundColor: '#27272A' }}
        >
          <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600 }}>
            {avgFrequency}x per week
          </span>
        </div>
        <div
          className="px-4 py-2 rounded-full flex items-center gap-2 whitespace-nowrap"
          style={{ backgroundColor: '#27272A' }}
        >
          <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600 }}>
            {intensityLabel.toUpperCase()} Intensity
          </span>
        </div>
      </div>

      {/* Average Effort Gauge */}
      {(() => {
        const cx = 130, cy = 120, r = 90;
        const startAngle = Math.PI * 0.8;   // ~144° — bottom-left
        const endAngle = Math.PI * 0.2;     // ~36°  — bottom-right
        const sweep = (2 * Math.PI - startAngle) + endAngle; // total arc span (~252°)
        const needleAngle = startAngle - (intensityPercent / 100) * sweep + 2 * Math.PI;
        const arcEnd = (frac: number) => {
          const a = startAngle - frac * sweep + 2 * Math.PI;
          return { x: cx + r * Math.cos(a), y: cy - r * Math.sin(a) };
        };
        const p0 = arcEnd(0);
        const pFill = arcEnd(intensityPercent / 100);
        const pEnd = arcEnd(1);
        const needleTip = { x: cx + (r - 18) * Math.cos(needleAngle), y: cy - (r - 18) * Math.sin(needleAngle) };
        const needleDot = { x: cx + (r + 2) * Math.cos(needleAngle), y: cy - (r + 2) * Math.sin(needleAngle) };
        const largeArcFill = (intensityPercent / 100) * sweep > Math.PI ? 1 : 0;
        const largeArcBg = sweep - (intensityPercent / 100) * sweep > Math.PI ? 1 : 0;

        return (
          <div
            className="p-5 mb-5"
            style={{
              backgroundColor: '#27272A',
              borderRadius: '16px',
              border: 'none',
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <span style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 600 }}>
                Average Effort
              </span>
              <span style={{ color: '#71717A', fontSize: '12px' }}>
                RIR {avgIntensity.toFixed(1)}
              </span>
            </div>

            <div className="flex items-center justify-center" style={{ position: 'relative', height: '170px' }}>
              <svg width="260" height="170" viewBox="0 0 260 170">
                <defs>
                  <linearGradient id="gaugeGrad" x1="0%" y1="50%" x2="100%" y2="50%">
                    <stop offset="0%" stopColor="#4ADE80" />
                    <stop offset="40%" stopColor="#FACC15" />
                    <stop offset="70%" stopColor="#FF6B35" />
                    <stop offset="100%" stopColor="#EF4444" />
                  </linearGradient>
                  <filter id="gaugeGlow">
                    <feGaussianBlur stdDeviation="4" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                  <filter id="needleGlow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Track (background arc) */}
                <path
                  d={`M ${p0.x} ${p0.y} A ${r} ${r} 0 1 1 ${pEnd.x} ${pEnd.y}`}
                  fill="none"
                  stroke="#1F1F23"
                  strokeWidth="14"
                  strokeLinecap="round"
                />

                {/* Filled arc */}
                <path
                  d={`M ${p0.x} ${p0.y} A ${r} ${r} 0 ${largeArcFill} 1 ${pFill.x} ${pFill.y}`}
                  fill="none"
                  stroke="url(#gaugeGrad)"
                  strokeWidth="14"
                  strokeLinecap="round"
                  filter="url(#gaugeGlow)"
                />

                {/* Tick marks */}
                {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
                  const a = startAngle - frac * sweep + 2 * Math.PI;
                  const inner = { x: cx + (r + 10) * Math.cos(a), y: cy - (r + 10) * Math.sin(a) };
                  const outer = { x: cx + (r + 16) * Math.cos(a), y: cy - (r + 16) * Math.sin(a) };
                  return (
                    <line
                      key={i}
                      x1={inner.x} y1={inner.y}
                      x2={outer.x} y2={outer.y}
                      stroke="#52525B"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  );
                })}

                {/* Needle */}
                <line
                  x1={cx} y1={cy}
                  x2={needleTip.x} y2={needleTip.y}
                  stroke="#F0F0F0"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  filter="url(#needleGlow)"
                />
                {/* Needle dot at arc edge */}
                <circle
                  cx={needleDot.x} cy={needleDot.y}
                  r="5"
                  fill="#F0F0F0"
                  filter="url(#needleGlow)"
                />
                {/* Center cap */}
                <circle cx={cx} cy={cy} r="6" fill="#3F3F46" />
                <circle cx={cx} cy={cy} r="3" fill="#71717A" />

                {/* Labels */}
                {[
                  { frac: 0, label: 'Light' },
                  { frac: 0.5, label: 'Moderate' },
                  { frac: 1, label: 'Max' },
                ].map(({ frac, label }, i) => {
                  const a = startAngle - frac * sweep + 2 * Math.PI;
                  const lx = cx + (r + 28) * Math.cos(a);
                  const ly = cy - (r + 28) * Math.sin(a);
                  return (
                    <text
                      key={i}
                      x={lx} y={ly}
                      fill="#52525B"
                      fontSize="10"
                      fontWeight="500"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      {label}
                    </text>
                  );
                })}
              </svg>

              {/* Center text overlay */}
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '55%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  pointerEvents: 'none',
                }}
              >
                <div style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 800, lineHeight: 1 }}>
                  {intensityLabel}
                </div>
                <div style={{ color: '#52525B', fontSize: '11px', fontWeight: 500, marginTop: '4px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  Avg Effort
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 12-Week Frequency Bar Chart */}
      <div
        className="p-5 mb-5"
        style={{
          backgroundColor: '#27272A',
          borderRadius: '16px',
          border: 'none'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <span style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 600 }}>
            Frequency (12 weeks)
          </span>
        </div>

        <div className="flex items-end justify-between gap-1.5" style={{ height: '120px' }}>
          {weeklyData.map((value, index) => (
            <div
              key={index}
              className="flex-1 rounded-t"
              style={{
                height: `${(value / 8) * 100}%`,
                backgroundColor: '#FF6B35',
                minHeight: '8px',
              }}
            ></div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3" style={{ fontSize: '10px', color: '#71717A' }}>
          <span>Week 1</span>
          <span>Week 12</span>
        </div>
      </div>

      {/* Activity Heatmap */}
      <div
        className="p-5 mb-5"
        style={{
          backgroundColor: '#27272A',
          borderRadius: '16px',
          border: 'none'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <span style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 600 }}>
            Activity Heatmap
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          {heatmapData.map((week, weekIndex) => (
            <div key={weekIndex} className="flex gap-1.5">
              {week.map((active, dayIndex) => (
                <div
                  key={dayIndex}
                  className="flex-1 rounded"
                  style={{
                    height: '20px',
                    backgroundColor: active ? '#FF6B35' : '#3F3F46',
                  }}
                ></div>
              ))}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between mt-3" style={{ fontSize: '10px', color: '#71717A' }}>
          <span>8 weeks ago</span>
          <span>Today</span>
        </div>
      </div>

      {/* Streak Card */}
      <div
        className="p-5 mb-5"
        style={{
          backgroundColor: '#27272A',
          borderRadius: '16px',
          border: 'none'
        }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Flame size={20} style={{ color: '#FF6B35' }} />
              <span style={{ color: '#F0F0F0', fontSize: '28px', fontWeight: 700 }}>{insights.streak.current}</span>
            </div>
            <span style={{ color: '#71717A', fontSize: '13px' }}>Day Streak</span>
          </div>
          <div className="text-right">
            <span style={{ color: '#F0F0F0', fontSize: '20px', fontWeight: 700 }}>{insights.streak.best}</span>
            <div style={{ color: '#71717A', fontSize: '13px' }}>Longest</div>
          </div>
        </div>
      </div>

      {/* Body Composition Line Chart */}
      {bodyCompositionData.length > 0 && (
        <div
          className="p-5 mb-5"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: 'none'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <span style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 600 }}>
              Body Composition
            </span>
          </div>

          <div className="relative" style={{ height: '120px', backgroundColor: '#1F2228', borderRadius: '8px', padding: '12px' }}>
            <svg width="100%" height="100%" viewBox="0 0 300 96" preserveAspectRatio="none">
              {bodyCompositionData.length > 1 && (
                <>
                  <polyline
                    points={bodyCompositionData.map((d, i) => {
                      const x = (i / (bodyCompositionData.length - 1)) * 300;
                      const minWeight = Math.min(...bodyCompositionData.map((bd) => bd.weight));
                      const maxWeight = Math.max(...bodyCompositionData.map((bd) => bd.weight));
                      const range = maxWeight - minWeight || 1;
                      const y = 96 - ((d.weight - minWeight) / range) * 96;
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="none"
                    stroke="#FFA033"
                    strokeWidth="2.5"
                  />
                  <polyline
                    points={bodyCompositionData.map((d, i) => {
                      const x = (i / (bodyCompositionData.length - 1)) * 300;
                      const minWeight = Math.min(...bodyCompositionData.map((bd) => bd.weight));
                      const maxWeight = Math.max(...bodyCompositionData.map((bd) => bd.weight));
                      const range = maxWeight - minWeight || 1;
                      const y = 96 - ((d.weight - minWeight) / range) * 96;
                      return `${x},${y}`;
                    }).join(' ')}
                    fill="url(#bodyGradient)"
                    opacity="0.2"
                  />
                </>
              )}
              <defs>
                <linearGradient id="bodyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#FFA033" />
                  <stop offset="100%" stopColor="#FFA033" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="flex items-center justify-between mt-3" style={{ fontSize: '10px', color: '#71717A' }}>
            <span>3 months ago</span>
            <span>Today: {latestWeight}{state.unitPreference === 'kg' ? 'kg' : ' lbs'}</span>
          </div>
        </div>
      )}

      {/* 1RM Trend Chart */}
      {insights.overload.length > 0 && (
        <div
          className="p-5 mb-5"
          style={{
            backgroundColor: '#27272A',
            borderRadius: '16px',
            border: 'none'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <span style={{ color: '#F0F0F0', fontSize: '15px', fontWeight: 600 }}>
              1RM Progression
            </span>
          </div>

          <div className="relative" style={{ height: '120px', backgroundColor: '#1F2228', borderRadius: '8px', padding: '12px' }}>
            <svg width="100%" height="100%" viewBox="0 0 300 96" preserveAspectRatio="none">
              {insights.overload.slice(0, 1).map((exercise) => {
                const trend = exercise.sessions > 1 ? [exercise.firstE1RM, exercise.lastE1RM] : [exercise.lastE1RM];
                return (
                  <g key={exercise.name}>
                    <polyline
                      points={trend.map((val, i) => {
                        const x = (i / Math.max(1, trend.length - 1)) * 300;
                        const minVal = Math.min(...trend);
                        const maxVal = Math.max(...trend);
                        const range = maxVal - minVal || 1;
                        const y = 96 - ((val - minVal) / range) * 96;
                        return `${x},${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#4ADE80"
                      strokeWidth="2.5"
                    />
                    <polyline
                      points={trend.map((val, i) => {
                        const x = (i / Math.max(1, trend.length - 1)) * 300;
                        const minVal = Math.min(...trend);
                        const maxVal = Math.max(...trend);
                        const range = maxVal - minVal || 1;
                        const y = 96 - ((val - minVal) / range) * 96;
                        return `${x},${y}`;
                      }).join(' ')}
                      fill="url(#rmGradient)"
                      opacity="0.2"
                    />
                  </g>
                );
              })}
              <defs>
                <linearGradient id="rmGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#4ADE80" />
                  <stop offset="100%" stopColor="#4ADE80" stopOpacity="0" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="flex items-center justify-between mt-3" style={{ fontSize: '10px', color: '#71717A' }}>
            <span>Volume</span>
            <span>1H</span>
            <span>1MO</span>
            <span>1YR</span>
            <span>ALL</span>
          </div>
        </div>
      )}

      {/* Personal Records */}
      {personalRecords.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-4">
            <span style={{ color: '#F0F0F0', fontSize: '13px', fontWeight: 600, letterSpacing: '0.5px' }}>
              PERSONAL RECORDS
            </span>
          </div>

          <div className="space-y-4">
            {personalRecords.map((group, groupIndex) => (
              <div
                key={groupIndex}
                className="overflow-hidden"
                style={{
                  backgroundColor: '#27272A',
                  borderRadius: '16px',
                  borderLeft: '4px solid #FF6B35',
                }}
              >
                <div className="p-4">
                  <div
                    className="px-2 py-1 rounded inline-block mb-3"
                    style={{ backgroundColor: '#FF6B35', color: '#18181B', fontSize: '11px', fontWeight: 700 }}
                  >
                    {group.category}
                  </div>

                  <div className="space-y-3">
                    {group.records.map((record, recordIndex) => (
                      <div key={recordIndex} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span style={{ color: '#F0F0F0', fontSize: '14px', fontWeight: 600 }}>
                              {record.name}
                            </span>
                            {record.isNew && (
                              <span
                                className="px-2 py-0.5 rounded"
                                style={{ backgroundColor: '#166534', color: '#4ADE80', fontSize: '10px', fontWeight: 700 }}
                              >
                                NEW
                              </span>
                            )}
                          </div>
                          <div className="flex items-baseline gap-2">
                            <span style={{ color: record.value === 'Not set' ? '#71717A' : '#F0F0F0', fontSize: '16px', fontWeight: 600 }}>
                              {record.value}
                            </span>
                            {record.value !== 'Not set' && (
                              <span style={{ color: '#4ADE80', fontSize: '13px', fontWeight: 600 }}>
                                {record.delta}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
